import { ArrowUpRight, Cpu } from "lucide-react";
import { LINKS, IMAGES } from "@/lib/constants";
import { TM } from "./TM";

const PRODUCTS = [
  {
    id: "shyft-app",
    name: "SHYFT App",
    tagline: "Your Financial Intelligence Tool",
    intro:
      "The SHYFT App is a financial tool that\u2019s specifically designed to help you pay closer attention to your money. This is the first and most important step to living a financially intelligent lifestyle.",
    image: IMAGES.heroMockup,
    imageStyle: "phone",
    bullets: [
      {
        title: "Documents Your Spending",
        sub: "This is the key to maximizing your personal tax savings potential.",
      },
      {
        title: "Protects Your Privacy",
        sub: "The data you enter is always saved on your device\u2019s storage.",
      },
      {
        title: "Affordable Pricing",
        sub: "This unique financial tool is only $10/month!",
      },
    ],
    pills: [
      { label: "Join Our Community", href: LINKS.community, style: "gold" },
      { label: "Activate the App", href: LINKS.appSubscription, style: "outline" },
    ],
  },
  {
    id: "orb",
    name: "SHYFT ORB",
    tagline: "Educational FinTech for Charting",
    intro:
      "Our 1-Year indicator suite is a real-time visual classroom \u2014 built to teach high-volume corridor mechanics while you educate yourself, and develop the skill of charting.",
    image: IMAGES.orbLogo,
    imageStyle: "logo",
    bullets: [
      {
        title: "Pre-Mapped Breakout Zones",
        sub: "Tokyo, London, and New York sessions printed directly on chart.",
      },
      {
        title: "1-Year Fixed License",
        sub: "365-day standalone access \u2014 no auto-renew, no surprises.",
      },
      {
        title: "Invite-Only Script Injection",
        sub: "Submit your TradingView username and we push the ORB to your account in 24 hours.",
      },
    ],
    pills: [
      { label: "Join Our Community", href: LINKS.community, style: "gold" },
      {
        label: "Acquire 1-Year License",
        href: LINKS.orbBreakout,
        style: "outline",
      },
    ],
  },
  {
    id: "smartlock",
    name: "SmartLock\u2122",
    tagline: "Pay Yourself First",
    intro:
      "SmartLock\u2122 is a highly flexible financial tool that allows you to earn dividends and guaranteed interest, for the rest of your life, on money you\u2019re spending today. SHYFT Community members have already \u2018SmartLocked\u2019 nearly $3 million! Are you next?",
    image: IMAGES.smartlockShield,
    imageStyle: "shield",
    bullets: [
      {
        title: "One Time Deposit",
        sub: "A single payment secures an income producing asset for life.",
      },
      {
        title: "Risk Free",
        sub: "SmartLock\u2122 contracts are fully insured assets, so you can\u2019t lose your money.",
      },
      {
        title: "Forever Money",
        sub: "SmartLock\u2122 contracts immediately multiply in value for your future generations.",
      },
    ],
    whyTitle: "Why SHYFT to SmartLock\u2122?",
    whyText:
      "It\u2019s never \u201cSmartLock my money, or\u2026\u201d \u2014 it\u2019s always \u201cSmartLock my money, then\u2026\u201d",
    pills: [
      { label: "Join Our Community", href: LINKS.community, style: "gold" },
      {
        label: "SmartLock\u2122 Consultation",
        href: LINKS.smartlock,
        style: "outline",
      },
    ],
  },
  {
    id: "eazybot",
    name: "EazyBot",
    tagline: "Everyone Needs Trading Bots",
    intro:
      "Trading Bots are software programs engineered to read different financial markets and execute profitable trades. EazyBot is a company that allows you to lease these powerful financial tools and put them to work for you and your family.",
    image: IMAGES.eazybotLogo,
    imageStyle: "bot",
    bullets: [
      {
        title: "No Upfront Costs",
        sub: "Everyone starts with a 45 day free trial.",
      },
      {
        title: "Non-Custodial System",
        sub: "You don\u2019t give EazyBot your money and hope they pay you back.",
      },
      {
        title: "Autopilot Your Income",
        sub: "The autopilot setting allows EazyBot to trade 24/7/365.",
      },
    ],
    whyTitle: "Why SHYFT to EazyBot?",
    whyText:
      "It\u2019s smarter to hire \u2018digital employees\u2019 to earn my income for me vs me doing it myself.",
    pills: [
      { label: "Join Our Community", href: LINKS.community, style: "gold" },
      { label: "More EazyBot Info", href: LINKS.eazybot, style: "outline" },
    ],
  },
];

/* Display order — follows weekly class schedule: App → SmartLock → EazyBot → Orb */
const DISPLAY_ORDER = ["shyft-app", "smartlock", "eazybot", "orb"];
const ORDERED_PRODUCTS = DISPLAY_ORDER.map(
  (id) => PRODUCTS.find((p) => p.id === id),
);
/** Visual wrapper per image type */
const ProductVisual = ({ image, imageStyle, name }) => {
  if (imageStyle === "phone") {
    // SHYFT App phone mockup — fills a portrait frame
    return (
      <div className="relative w-full max-w-[280px] sm:max-w-[320px] aspect-[9/19] mx-auto rounded-[2rem] overflow-hidden border border-[rgba(201,162,91,0.45)] bg-[#0a0a0a] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(201,162,91,0.18)_inset] product-breathe">
        <div
          aria-hidden
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 40% at 50% 18%, rgba(30,144,255,0.32), transparent 70%), radial-gradient(80% 50% at 50% 0%, rgba(231,201,138,0.22), transparent 70%)",
          }}
        />
        <img
          src={image}
          alt={`${name} mockup`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  // Logo / shield / bot — circular dark frame with gold-blue glow
  const inner = "absolute inset-3 rounded-full overflow-hidden";

  return (
    <div className="relative w-full max-w-[320px] aspect-square mx-auto">
      {/* Outer glow */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(30,144,255,0.32), rgba(201,162,91,0.15) 55%, transparent 75%)",
          filter: "blur(20px)",
        }}
      />
      {/* Frame */}
      <div className="relative w-full h-full rounded-full border border-[rgba(201,162,91,0.55)] bg-gradient-to-br from-[#1c1814] to-[#0a0a0a] shadow-[0_30px_70px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(201,162,91,0.18)_inset] product-breathe overflow-hidden">
        <div className={inner}>
          <img
            src={image}
            alt={`${name} logo`}
            className="w-full h-full object-contain p-6"
            style={
              imageStyle === "shield"
                ? { filter: "drop-shadow(0 0 22px rgba(201,162,91,0.35))" }
                : undefined
            }
            loading="lazy"
          />
        </div>
        {/* Inner ring */}
        <div
          aria-hidden
          className="absolute inset-2 rounded-full border border-[rgba(30,144,255,0.35)] pointer-events-none"
        />
      </div>
    </div>
  );
};

const ProductRow = ({ product, index }) => {
  const isReversed = index % 2 === 1;
  return (
    <article
      data-testid={`fintech-${product.id}`}
      className="reveal relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center"
    >
      <div
        className={`lg:col-span-5 order-1 ${
          isReversed ? "lg:order-2" : "lg:order-1"
        }`}
      >
        <ProductVisual {...product} />
      </div>

      <div
        className={`lg:col-span-7 order-2 ${
          isReversed ? "lg:order-1" : "lg:order-2"
        }`}
      >
        <div className="eyebrow !text-[#6fb3ff]">{product.tagline}</div>
        <h3 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.1] text-white">
          <span className="text-gradient-gold">
            {product.name.replace(/\u2122$/, "")}
          </span>
          {product.name.endsWith("\u2122") ? <TM color="#c9a25b" /> : null}
        </h3>
        <p className="mt-4 text-[#cfc8b9] text-base md:text-[17px] leading-[1.7]">
          {product.intro}
        </p>

        <ul className="mt-7 space-y-5">
          {product.bullets.map((b) => (
            <li key={b.title} className="flex gap-4">
              <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full border border-[rgba(201,162,91,0.35)] bg-[#14110e] shrink-0">
                <span className="status-dot" aria-hidden />
              </span>
              <div>
                <div className="text-white font-semibold text-[15px]">
                  {b.title}
                </div>
                <div className="text-[#b8b3aa] text-sm mt-1 leading-relaxed">
                  {b.sub}
                </div>
              </div>
            </li>
          ))}
        </ul>

        {product.whyTitle && (
          <div className="mt-7 rounded-2xl border border-[rgba(30,144,255,0.35)] bg-[rgba(30,144,255,0.05)] p-5">
            <div className="eyebrow !text-[#6fb3ff]">{product.whyTitle}</div>
            <p className="mt-2 text-white text-[15px] leading-relaxed">
              {product.whyText}
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-4">
          {product.pills.map((p) => (
            <a
              key={p.label}
              href={p.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`fintech-${product.id}-pill-${p.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "")}`}
              className={p.style === "gold" ? "btn-gold" : "btn-outline-gold"}
            >
              {p.label}
              <ArrowUpRight size={16} strokeWidth={2.4} />
            </a>
          ))}
        </div>
      </div>
    </article>
  );
};

export const FintechSection = () => {
  return (
    <section
      id="tradingview"
      data-testid="fintech-section"
      className="relative py-16 md:py-24"
    >
      <div
        aria-hidden
        className="vault-spotlight-cyan"
        style={{ top: "10%", right: "-10%", width: 620, height: 620, opacity: 0.45 }}
      />
      <div
        aria-hidden
        className="vault-spotlight"
        style={{ top: "30%", left: "-8%", width: 520, height: 520, opacity: 0.45 }}
      />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Eyebrow */}
        <div className="reveal text-center">
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[rgba(30,144,255,0.35)] bg-[#0b1422]/70 backdrop-blur">
            <Cpu size={14} className="text-[#6fb3ff]" />
            <span className="eyebrow !text-[#6fb3ff]">FinTech</span>
            <span className="ml-1 status-dot" aria-hidden />
          </div>
          <h2 className="font-serif-display mt-6 text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.1] text-white">
            Strategic <span className="text-gradient-gold">FinTech</span>{" "}
            Tools
          </h2>
        </div>

        {/* Product rows */}
        <div className="mt-16 md:mt-20 space-y-24 md:space-y-28">
          {ORDERED_PRODUCTS.map((p, i) => (
            <ProductRow key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FintechSection;
